import { generateID } from 'src/utils/random';
import type {
  QueueItem,
  QueueItemInput,
  QueueItemStatus,
  SyncOptions,
  SyncResult,
  OfflineConfig,
} from './types';
import { idbQueueStorage } from './storage/idb-queue-storage';

/**
 * Sync Manager
 * Manages the offline queue and synchronization process
 * Singleton pattern to ensure single instance across app
 */

const DEFAULT_CONFIG: OfflineConfig = {
  maxRetries: 3,
  retryDelay: 5000, // 5 seconds
  maxQueueSize: 1000,
  autoSync: true,
  syncOnReconnect: true,
};

// Sync handler type
type SyncHandler = (item: QueueItem) => Promise<void>;

class SyncManager {
  private static instance: SyncManager | null = null;
  private config: OfflineConfig;
  private isProcessing: boolean = false;
  private listeners: Set<(stats: { status: QueueItemStatus; item: QueueItem }) => void> = new Set();
  private syncHandlers: Map<string, SyncHandler> = new Map();

  private constructor(config: Partial<OfflineConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(config?: Partial<OfflineConfig>): SyncManager {
    if (!SyncManager.instance) {
      SyncManager.instance = new SyncManager(config);
    }
    return SyncManager.instance;
  }

  /**
   * Reset the singleton (mainly for testing)
   */
  public static reset(): void {
    SyncManager.instance = null;
  }

  /**
   * Add an item to the sync queue
   */
  public async enqueue(input: QueueItemInput): Promise<QueueItem> {
    const stats = await idbQueueStorage.getStats();

    if (stats.total >= this.config.maxQueueSize) {
      throw new Error(`Queue is full (max ${this.config.maxQueueSize} items)`);
    }

    const now = new Date();
    const item: QueueItem = {
      id: `queue_${generateID(12)}`,
      type: input.type,
      status: 'pending',
      priority: input.priority ?? 5,
      createdAt: now,
      updatedAt: now,
      retryCount: 0,
      maxRetries: input.maxRetries ?? this.config.maxRetries,
      data: input.data,
      optimisticId: input.optimisticId,
      rollbackData: input.rollbackData,
    };

    await idbQueueStorage.addItem(item);
    this.notifyListeners('pending', item);

    return item;
  }

  /**
   * Process the queue and sync pending items
   */
  public async sync(options: SyncOptions = {}): Promise<SyncResult[]> {
    if (this.isProcessing) {
      console.warn('[SyncManager] Sync already in progress');
      return [];
    }

    this.isProcessing = true;
    const results: SyncResult[] = [];

    try {
      // Get pending items
      let items = [...await idbQueueStorage.getPendingItems(), ...await idbQueueStorage.getRetryingItems()];

      // If retryFailed is enabled, also get failed items that can be retried
      if (options.retryFailed) {
        const failedItems = await idbQueueStorage.getFailedItems();
        console.log("get failedItems", failedItems)
        items = [...items, ...failedItems];
      }

      // Limit batch size
      const batchSize = options.batchSize ?? 10;
      const batch = items.slice(0, batchSize);

      for (const item of batch) {
        try {
          options.onItemStart?.(item);
          await this.processItem(item);
          results.push({ success: true, itemId: item.id, data: item.data });
          options.onItemSuccess?.(item, item.data);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          results.push({ success: false, itemId: item.id, error: errorMessage });
          options.onItemFailure?.(item, error as Error);
        }
      }

      options.onComplete?.(results);
      return results;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process a single queue item
   */
  private async processItem(item: QueueItem): Promise<void> {
    // Mark as processing
    await this.updateItemStatus(item.id, 'processing');

    try {
      // Execute the sync operation based on item type
      await this.executeSync(item);

      // Mark as completed
      await this.updateItemStatus(item.id, 'completed');
    } catch (error) {
      const retryCount = item.retryCount + 1;

      if (retryCount >= item.maxRetries) {
        // Mark as failed
        await idbQueueStorage.updateItem(item.id, {
          status: 'failed',
          retryCount,
          error: error instanceof Error ? error.message : 'Unknown error',
          lastErrorAt: new Date(),
        });
        this.notifyListeners('failed', { ...item, status: 'failed' });
        throw error;
      } else {
        // Mark for retry
        await idbQueueStorage.updateItem(item.id, {
          status: 'retrying',
          retryCount,
          error: error instanceof Error ? error.message : 'Unknown error',
          lastErrorAt: new Date(),
        });
        this.notifyListeners('retrying', { ...item, status: 'retrying' });
        throw error;
      }
    }
  }

  /**
   * Execute the actual sync operation
   * Uses registered handlers if available, otherwise falls back to default behavior
   */
  private async executeSync(item: QueueItem): Promise<void> {
    // Check if we have a registered handler for this type
    const handler = this.syncHandlers.get(item.type);
    if (handler) {
      await handler(item);
      return;
    }

    // Fallback to default behavior (placeholder)
    switch (item.type) {
      case 'invoice_create':
        console.log('[SyncManager] Creating invoice:', item.data);
        break;
      case 'invoice_update':
        console.log('[SyncManager] Updating invoice:', item.data);
        break;
      case 'customer_create':
        console.log('[SyncManager] Creating customer:', item.data);
        break;
      case 'transaction_create':
        console.warn('[SyncManager] No handler registered for transaction_create');
        throw new Error('No sync handler registered for transaction_create');
      default:
        console.warn('[SyncManager] Unknown item type:', item.type);
        throw new Error(`Unknown item type: ${item.type}`);
    }

    // Simulate network delay for placeholder implementations
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  /**
   * Update item status and notify listeners
   */
  private async updateItemStatus(id: string, status: QueueItemStatus): Promise<void> {
    await idbQueueStorage.updateItem(id, { status });
    const item = await idbQueueStorage.getItem(id);
    if (item) {
      this.notifyListeners(status, item);
    }
  }

  /**
   * Retry a failed item
   */
  public async retryItem(id: string): Promise<void> {
    const item = await idbQueueStorage.getItem(id);
    if (!item) {
      throw new Error(`Item ${id} not found`);
    }

    if (item.status !== 'failed' && item.status !== 'retrying') {
      throw new Error(`Cannot retry item with status ${item.status}`);
    }

    await idbQueueStorage.updateItem(id, {
      status: 'pending',
      error: undefined,
      lastErrorAt: undefined,
    });

    this.notifyListeners('pending', { ...item, status: 'pending' });
  }

  /**
   * Remove an item from the queue
   */
  public async removeItem(id: string): Promise<void> {
    await idbQueueStorage.removeItem(id);
  }

  /**
   * Clear all completed items
   */
  public async clearCompleted(): Promise<number> {
    return await idbQueueStorage.clearCompleted();
  }

  /**
   * Get queue statistics
   */
  public async getStats() {
    return await idbQueueStorage.getStats();
  }

  /**
   * Get all items in the queue
   */
  public async getAllItems() {
    return await idbQueueStorage.getAllItems();
  }

  /**
   * Get pending items
   */
  public async getPendingItems() {
    return await idbQueueStorage.getPendingItems();
  }

  /**
   * Subscribe to queue changes
   */
  public subscribe(callback: (stats: { status: QueueItemStatus; item: QueueItem }) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of a status change
   */
  private notifyListeners(status: QueueItemStatus, item: QueueItem): void {
    this.listeners.forEach((callback) => {
      try {
        callback({ status, item });
      } catch (error) {
        console.error('[SyncManager] Error in listener callback:', error);
      }
    });
  }

  /**
   * Check if sync is in progress
   */
  public getIsProcessing(): boolean {
    return this.isProcessing;
  }

  /**
   * Register a sync handler for a specific item type
   */
  public registerSyncHandler(type: string, handler: SyncHandler): void {
    this.syncHandlers.set(type, handler);
  }

  /**
   * Unregister a sync handler
   */
  public unregisterSyncHandler(type: string): void {
    this.syncHandlers.delete(type);
  }

  public getHandler(type: string): SyncHandler|undefined {
    return this.syncHandlers.get(type)
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<OfflineConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  public getConfig(): OfflineConfig {
    return { ...this.config };
  }
}

export default SyncManager;
export { DEFAULT_CONFIG };
